import {Component, Input} from '@angular/core';
import {CommonModule} from '@angular/common';

/** Categorical values an observation area can take — see WeeklyDirection on
 *  patient-weekly-snapshot.model.ts. Mapped to +1 / 0 / -1 before plotting. */
export type SparklineDirection = 'better' | 'noChange' | 'worse';

interface SparklinePoint {
    index: number;
    value: number;
    x: number;
    y: number;
}

interface SparklineSegment {
    pointsAttr: string;
}

const WIDTH = 120;
const HEIGHT = 48;
const PADDING = 4;
const ZERO_TICK_HALF_HEIGHT = 4;

/**
 * Shared weekly trend sparkline (UNIVAPLAT-3034). Renders a small inline-SVG
 * line chart across the standard 7-ish week/day slots used by every v2 weekly
 * panel.
 *
 * A missing week/day must never be plotted as 0 — a `null` entry in `points`
 * is simply skipped when building the line, so the line stays one continuous
 * stroke from the first to the last real value instead of breaking into gaps
 * (no dot is drawn at the missing index, so it's still distinguishable from an
 * answered point on hover/inspection — just not as a visible break in the
 * line). A real value of 0 is still real data, so it renders as both part of
 * the line AND a small vertical tick, distinguishing "answered zero" from "no
 * data".
 *
 * Two input modes:
 *  - `points`: raw numeric values, `null` = gap.
 *  - `categorical`: for observation areas — `better`/`noChange`/`worse`/`null`
 *    mapped internally to +1/0/-1/null before plotting.
 *
 * `role="img"` plus a generated `aria-label` (or an explicit `ariaLabel`
 * override) describes every value, including which weeks have no data, so the
 * chart is never SVG-shaped decoration with no accessible equivalent.
 */
@Component({
    selector: 'cdss-weekly-trend-sparkline',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './weekly-trend-sparkline.component.html',
    styleUrl: './weekly-trend-sparkline.component.scss'
})
export class WeeklyTrendSparklineComponent {

    readonly width = WIDTH;
    readonly height = HEIGHT;

    /** Explicit scale override — otherwise derived from the non-null values (plus 0). */
    @Input() min?: number;
    @Input() max?: number;

    /** Overrides the generated aria-label entirely. */
    @Input() ariaLabel?: string;

    /**
     * Optional per-point labels (e.g. weekday or a short week-start date), used to build the
     * generated aria-label. Also rendered as visible X-axis text when `showLabels` is true (opt-in
     * — the Stress sparkline on the same page shares this component but was not asked to grow an
     * axis, so this defaults to off).
     */
    @Input() pointLabels?: (string | null)[];

    /** Renders `pointLabels` as visible text under the line — see `pointLabels` doc. */
    @Input() showLabels = false;

    private _values: (number | null)[] = [];

    @Input()
    set points(value: (number | null)[] | null) {
        this._values = value ?? [];
    }

    get points(): (number | null)[] {
        return this._values;
    }

    /** Categorical mode for observation areas — see class doc. */
    @Input()
    set categorical(value: (SparklineDirection | null)[] | null) {
        this._values = (value ?? []).map(v => WeeklyTrendSparklineComponent.directionToValue(v));
    }

    static directionToValue(direction: SparklineDirection | null): number | null {
        switch (direction) {
            case 'better':
                return 1;
            case 'worse':
                return -1;
            case 'noChange':
                return 0;
            default:
                return null;
        }
    }

    private get range(): { min: number; max: number } {
        const nonNull = this._values.filter((v): v is number => v !== null);
        const min = this.min ?? (nonNull.length ? Math.min(...nonNull, 0) : 0);
        const max = this.max ?? (nonNull.length ? Math.max(...nonNull, 0) : 1);
        return max > min ? {min, max} : {min, max: min + 1};
    }

    private valueToY(value: number): number {
        const {min, max} = this.range;
        const ratio = (value - min) / (max - min);
        return HEIGHT - PADDING - ratio * (HEIGHT - PADDING * 2);
    }

    private xForIndex(index: number): number {
        const count = this._values.length;
        if (count <= 1) return 0;
        return (index / (count - 1)) * WIDTH;
    }

    /** Every non-null point, plotted. */
    get plottedPoints(): SparklinePoint[] {
        return this._values
            .map((value, index) => (value === null ? null : {
                index,
                value,
                x: this.xForIndex(index),
                y: this.valueToY(value)
            }))
            .filter((p): p is SparklinePoint => p !== null);
    }

    /** Zero-valued points get a distinct tick mark instead of (as well as) a plain dot. */
    get zeroPoints(): SparklinePoint[] {
        return this.plottedPoints.filter(p => p.value === 0);
    }

    get nonZeroPoints(): SparklinePoint[] {
        return this.plottedPoints.filter(p => p.value !== 0);
    }

    /**
     * Every index (not just plotted/non-null ones) gets an X-axis label — a "no data" week still
     * has a known week-start and occupies a real X position, so its label stays visible too.
     */
    get labelPoints(): { index: number; x: number }[] {
        return this._values.map((_, index) => ({index, x: this.xForIndex(index)}));
    }

    /** Baseline Y for the visible label text — just below the line's drawing area. */
    get labelY(): number {
        return HEIGHT + 10;
    }

    /** A single continuous line through every non-null value, in index order —
     *  null entries are skipped (not plotted as 0, not drawn as a break), so the
     *  line reads as one unbroken stroke from the first to the last real value
     *  regardless of how many days in between have no data. */
    get segments(): SparklineSegment[] {
        const points = this.plottedPoints;
        if (points.length < 2) return [];
        return [{pointsAttr: points.map(p => `${p.x},${p.y}`).join(' ')}];
    }

    get computedAriaLabel(): string {
        if (this.ariaLabel) return this.ariaLabel;
        const parts = this._values.map((value, index) => {
            const label = this.pointLabels?.[index] ?? `week ${index + 1}`;
            return value === null ? `${label}: no data` : `${label}: ${value}`;
        });
        return `Trend: ${parts.join(', ')}`;
    }

    trackSegment(index: number): number {
        return index;
    }

    trackPoint(_index: number, point: SparklinePoint): number {
        return point.index;
    }
}
